// src/services/LoggerService.js
import { APP_VERSION } from "@/config";
import api from "@/config/api";

export default class LoggerService {
    static _cachedIp = null;

    /**
     * Get user's IP address from external service
     * Caches the result so only one external call is made per session
     */
    static async getClientIpAddress() {
      if (this._cachedIp) return this._cachedIp;
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        this._cachedIp = data.ip || 'unknown';
        return this._cachedIp;
      } catch (error) {
        console.error("Failed to fetch IP address:", error);
        return 'unknown';
      }
    }

    /**
     * Pre-fetch and cache the IP address (call early in app lifecycle)
     */
    static async prefetchIp() {
      await this.getClientIpAddress();
    }

    /**
     * Get basic context information
     */
    static getContext() {
      return {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        version: APP_VERSION
      };
    }

    /**
     * Log general information
     */
    static async logInfo(message, additionalData = {}) {
      const ipAddress = await this.getClientIpAddress();
      const context = this.getContext();
      
      const logData = {
        ipAddress,
        ...context,
        ...additionalData
      };
      
      this.sendLog("loginfo", message, logData);
    }
  
    /**
     * Log error messages
     */
    static async logError(message, additionalData = {}) {
      const ipAddress = await this.getClientIpAddress();
      const context = this.getContext();
      
      const logData = {
        ipAddress,
        ...context,
        ...additionalData
      };
      
      this.sendLog("ajaxerror", message, logData);
    }

    /**
     * Log authentication attempts (login, registration, forgot password)
     */
    static async logAuthAttempt(action, email, success = true, errorMessage = null, additionalData = {}) {
      const ipAddress = await this.getClientIpAddress();
      const context = this.getContext();
      
      const logData = {
        type: 'AUTH',
        action,
        email,
        success,
        ipAddress,
        errorMessage,
        ...context,
        ...additionalData
      };

      const message = `AUTH: ${action} - ${email} - ${success ? 'SUCCESS' : 'FAILED'}${errorMessage ? ` - ${errorMessage}` : ''} - IP: ${ipAddress}`;
      
      // Use logInfo for successful auth, logError for failures
      if (success) {
        this.sendLog("loginfo", message, logData);
      } else {
        this.sendLog("ajaxerror", message, logData);
      }
    }

    /**
     * Log data operations (insert, update, delete)
     */
    static async logDataOperation(operation, entityType, entityId, data = {}, userId = null) {
      const ipAddress = await this.getClientIpAddress();
      const context = this.getContext();
      
      const logData = {
        type: 'DATA_OPERATION',
        operation, // 'INSERT', 'UPDATE', 'DELETE'
        entityType, // 'user', 'activity', 'workgroup', etc.
        entityId,
        userId,
        ipAddress,
        data,
        ...context
      };

      const message = `DATA: ${operation} ${entityType} (ID: ${entityId}) - User: ${userId || 'unknown'} - IP: ${ipAddress}`;
      
      // Use logInfo for successful operations, logError for failures
      if (operation.includes('FAILED')) {
        this.sendLog("ajaxerror", message, logData);
      } else {
        this.sendLog("loginfo", message, logData);
      }
    }

    /**
     * Log UI errors
     */
    static async logUIError(error, componentName = null, additionalContext = {}) {
      const ipAddress = await this.getClientIpAddress();
      const context = this.getContext();
      
      const errorData = {
        type: 'UI_ERROR',
        message: error.message || error.toString(),
        stack: error.stack,
        componentName,
        ipAddress,
        ...context,
        ...additionalContext
      };

      const logMessage = `UI ERROR: ${error.message || error.toString()}${componentName ? ` in ${componentName}` : ''} - IP: ${ipAddress}`;
      
      this.sendLog("ajaxerror", logMessage, errorData);
    }

    /**
     * Log API errors
     */
    static async logAPIError(endpoint, method, error, requestData = {}) {
      const ipAddress = await this.getClientIpAddress();
      const context = this.getContext();
      
      const errorData = {
        type: 'API_ERROR',
        endpoint,
        method,
        error: error.message || error.toString(),
        requestData,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
        ipAddress,
        ...context
      };
      const peopleGroups422 = method === 'GET'
        && endpoint === '/people-groups'
        && error.response?.status === 422;
      const queryParameterNames = peopleGroups422 && error.config?.params
        ? Object.keys(error.config.params).join(',') || 'none'
        : null;
      const validationFieldNames = peopleGroups422
        ? Object.keys(error.response?.data?.errors || {}).join(',') || 'none'
        : null;
      const diagnostics = peopleGroups422
        ? ` - Query parameter names: ${queryParameterNames} - Validation fields: ${validationFieldNames}`
        : '';
      const message = `API ERROR: ${method} ${endpoint} - ${error.message || error.toString()}${diagnostics} - IP: ${ipAddress}`;
      
      this.sendLog("ajaxerror", message, errorData);
    }
  
    /**
     * Send log to server
     */
    static async sendLog(cmd, message) {
      try {
        const formattedMessage = `[ #CS-MF-Dashboard(v${APP_VERSION}) ] --> ${message}`;
        
        await api.post(`/personajax`, {
          cmd: cmd,
          message: formattedMessage
        });
      } catch (error) {
        // Silently fail to prevent infinite loop
        console.error("Error sending log to server:", error);
      }
    }
  }
  